import {
  FRENCH_CROPPED_DAY_NAMES,
  FRENCH_MONTH_NAMES,
  isDayAfter,
} from "@ac-expo/utils";
import { addDays, differenceInDays, isBefore } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";

export type PeriodCalendarDay = {
  date: Date;
  formattedDay: string;
  formattedDate: string;
};

export type PeriodCalendarPeriod = {
  startDate: Date;
  endDate: Date;
};

export type UsePeriodCalendarOptions = {
  initialDate?: string | Date | null;
  maxDaysPerPeriod?: number;
  mode?: "period" | "scroll";
  scrollDaysToLoad?: number;
  centerOnInitialDate?: boolean;
};

export type UsePeriodCalendarReturn = {
  visibleDays: PeriodCalendarDay[];
  selectedDay: Date;
  currentPeriod: PeriodCalendarPeriod;
  today: Date;
  handlePrev: () => void;
  handleNext: () => void;
  handleDateChange: (date: Date) => void;
  canNavigatePrev: boolean;
  canNavigateNext: boolean;
  formattedMonth: string;
  formattedYear: string;
  loadMoreDays: (direction: "prev" | "next") => number;
};

const normalizeDate = (value: Date | string): Date => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const createVisibleDays = (
  startDate: Date,
  endDate: Date
): PeriodCalendarDay[] => {
  const days: PeriodCalendarDay[] = [];
  const total = differenceInDays(endDate, startDate);

  if (total < 0) {
    return days;
  }

  for (let i = 0; i <= total; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    date.setHours(0, 0, 0, 0);
    const dayOfWeek = date.getDay();

    days.push({
      date,
      formattedDay: FRENCH_CROPPED_DAY_NAMES[dayOfWeek],
      formattedDate: String(date.getDate()),
    });
  }

  return days;
};

const getPeriod = (
  date: Date,
  maxDaysPerPeriod: number = 8
): PeriodCalendarPeriod => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);

  const day = normalizedDate.getDate();
  const lastDayOfMonth = new Date(
    normalizedDate.getFullYear(),
    normalizedDate.getMonth() + 1,
    0
  );

  const periodBeginning = addDays(
    normalizedDate,
    -((day - 1) % maxDaysPerPeriod)
  );
  const fullPeriodAfterBeginning = addDays(
    periodBeginning,
    maxDaysPerPeriod - 1
  );
  const periodEnd = isBefore(fullPeriodAfterBeginning, lastDayOfMonth)
    ? fullPeriodAfterBeginning
    : lastDayOfMonth;

  return { startDate: periodBeginning, endDate: periodEnd };
};

export const usePeriodCalendar = ({
  initialDate,
  maxDaysPerPeriod = 8,
  mode = "period",
  scrollDaysToLoad = 30,
  centerOnInitialDate = false,
}: UsePeriodCalendarOptions = {}): UsePeriodCalendarReturn => {
  const today = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  const initialDateObj = useMemo(() => {
    if (!initialDate) return today;
    return normalizeDate(initialDate);
  }, [initialDate, today]);

  const [selectedDay, setSelectedDay] = useState<Date>(initialDateObj);

  const initialPeriod = useMemo<PeriodCalendarPeriod>(() => {
    if (mode === "scroll") {
      const minimumStart = addDays(today, -5);
      const baseDate = initialDateObj;

      if (centerOnInitialDate && isDayAfter(baseDate, today)) {
        const daysAround = Math.floor(scrollDaysToLoad / 2);
        const startCandidate = addDays(baseDate, -2);
        const endCandidate = addDays(baseDate, daysAround);

        return {
          startDate: startCandidate,
          endDate: endCandidate,
        };
      }

      let startCandidate = addDays(baseDate, -Math.floor(scrollDaysToLoad / 2));

      if (isBefore(startCandidate, minimumStart)) {
        startCandidate = new Date(minimumStart);
      }

      let endCandidate = addDays(startCandidate, scrollDaysToLoad - 1);

      if (isDayAfter(baseDate, endCandidate)) {
        const diff = differenceInDays(baseDate, endCandidate);
        startCandidate = addDays(startCandidate, diff);
        endCandidate = addDays(startCandidate, scrollDaysToLoad - 1);
      }

      return {
        startDate: startCandidate,
        endDate: endCandidate,
      };
    }
    return getPeriod(initialDateObj, maxDaysPerPeriod);
  }, [
    initialDateObj,
    maxDaysPerPeriod,
    mode,
    scrollDaysToLoad,
    today,
    centerOnInitialDate,
  ]);

  const [currentPeriod, setCurrentPeriod] =
    useState<PeriodCalendarPeriod>(initialPeriod);

  const [visibleDays, setVisibleDays] = useState<PeriodCalendarDay[]>(() =>
    createVisibleDays(initialPeriod.startDate, initialPeriod.endDate)
  );

  useEffect(() => {
    if (mode === "period") {
      setVisibleDays(
        createVisibleDays(currentPeriod.startDate, currentPeriod.endDate)
      );
    }
  }, [currentPeriod, mode]);

  const canNavigatePrev = useMemo(() => {
    if (mode === "scroll") return false;
    return isDayAfter(currentPeriod.startDate, today);
  }, [currentPeriod.startDate, mode, today]);

  const canNavigateNext = mode === "scroll" ? false : true;

  const handlePrev = useCallback(() => {
    if (mode === "scroll") return;

    setCurrentPeriod((prev) => {
      if (!isDayAfter(prev.startDate, today)) return prev;
      return getPeriod(addDays(prev.startDate, -1), maxDaysPerPeriod);
    });
  }, [mode, today, maxDaysPerPeriod]);

  const handleNext = useCallback(() => {
    if (mode === "scroll") return;

    setCurrentPeriod((prev) => {
      return getPeriod(addDays(prev.endDate, 1), maxDaysPerPeriod);
    });
  }, [maxDaysPerPeriod, mode]);

  const handleDateChange = useCallback((date: Date) => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    setSelectedDay(newDate);
  }, []);

  const loadMoreDays = useCallback(
    (direction: "prev" | "next") => {
      if (mode !== "scroll") return 0;

      if (direction === "prev") {
        const earliestVisible = currentPeriod.startDate;
        const tentativeStart = addDays(earliestVisible, -scrollDaysToLoad);
        const clampedStart = isBefore(tentativeStart, today)
          ? today
          : tentativeStart;
        const newDays = createVisibleDays(
          clampedStart,
          addDays(earliestVisible, -1)
        );

        if (newDays.length === 0) {
          return 0;
        }

        let added = 0;
        setVisibleDays((prevDays) => {
          const existingTimes = new Set(
            prevDays.map((day) => day.date.getTime())
          );
          const filtered: PeriodCalendarDay[] = [];

          for (const day of newDays) {
            const time = day.date.getTime();
            if (!existingTimes.has(time)) {
              existingTimes.add(time);
              filtered.push(day);
            }
          }

          added = filtered.length;
          if (!added) {
            return prevDays;
          }

          return [...filtered, ...prevDays];
        });

        if (!added) {
          return 0;
        }

        setCurrentPeriod((prev) => ({
          startDate: clampedStart,
          endDate: prev.endDate,
        }));

        return added;
      }

      const latestVisible = currentPeriod.endDate;
      const newEnd = addDays(latestVisible, scrollDaysToLoad);
      const nextDays = createVisibleDays(addDays(latestVisible, 1), newEnd);

      if (nextDays.length === 0) {
        return 0;
      }

      let added = 0;
      setVisibleDays((prevDays) => {
        const existingTimes = new Set(
          prevDays.map((day) => day.date.getTime())
        );
        const filtered: PeriodCalendarDay[] = [];

        for (const day of nextDays) {
          const time = day.date.getTime();
          if (!existingTimes.has(time)) {
            existingTimes.add(time);
            filtered.push(day);
          }
        }

        added = filtered.length;
        if (!added) {
          return prevDays;
        }

        return [...prevDays, ...filtered];
      });

      if (!added) {
        return 0;
      }

      setCurrentPeriod((prev) => ({
        startDate: prev.startDate,
        endDate: newEnd,
      }));

      return added;
    },
    [currentPeriod, mode, scrollDaysToLoad, today]
  );

  useEffect(() => {
    setSelectedDay((prev) => {
      if (prev.getTime() === initialDateObj.getTime()) {
        return prev;
      }
      return new Date(initialDateObj);
    });
  }, [initialDateObj]);

  useEffect(() => {
    if (mode !== "period") return;

    setCurrentPeriod((prev) => {
      const hasSameRange =
        prev.startDate.getTime() === initialPeriod.startDate.getTime() &&
        prev.endDate.getTime() === initialPeriod.endDate.getTime();

      if (hasSameRange) {
        return prev;
      }

      return {
        startDate: new Date(initialPeriod.startDate),
        endDate: new Date(initialPeriod.endDate),
      };
    });
  }, [initialPeriod, mode]);

  useEffect(() => {
    if (mode !== "scroll") return;

    setCurrentPeriod((prev) => {
      const hasSameRange =
        prev.startDate.getTime() === initialPeriod.startDate.getTime() &&
        prev.endDate.getTime() === initialPeriod.endDate.getTime();

      if (hasSameRange) {
        return prev;
      }

      return {
        startDate: new Date(initialPeriod.startDate),
        endDate: new Date(initialPeriod.endDate),
      };
    });
    setVisibleDays(
      createVisibleDays(initialPeriod.startDate, initialPeriod.endDate)
    );
  }, [initialPeriod, mode]);

  const formattedMonth = useMemo(() => {
    const month = selectedDay.getMonth();
    return FRENCH_MONTH_NAMES[month].toUpperCase();
  }, [selectedDay]);

  const formattedYear = useMemo(() => {
    return String(selectedDay.getFullYear());
  }, [selectedDay]);

  return {
    visibleDays,
    selectedDay,
    currentPeriod,
    today,
    handlePrev,
    handleNext,
    handleDateChange,
    canNavigatePrev,
    canNavigateNext,
    formattedMonth,
    formattedYear,
    loadMoreDays,
  };
};
